import { Button, Card } from '@alifd/next';
import { DatePicker, message, Select, Spin, Table } from 'antd';
import type { RangePickerProps } from 'antd/lib/date-picker';
import React, { useMemo, useState } from 'react';
import useSWR from 'swr';

import EditCategories from '@/components/UI/desktop/EditCategories';

import DetailModal from './components/DetailModal';
import RouteFormModal from './components/RouteFormModal';
import TableActionButtons from './components/TableActionButtons';
import { COLUMNS, DEFAULT_OPTION } from './constants';
import {
  checkIsValidPeriod,
  fetchInspectionRouteList,
  fetchRouteFilterOptions,
  getSortedRouteOptions,
  getTableColumns,
  handleDeleteConfirm,
  showQrcode,
} from './helpers';
import styles from './index.module.scss';
import type { RecordInTable } from './types';

const DateRangePicker = DatePicker.RangePicker;
const pageSize = 10;

const EquipmentInspectionManagement = () => {
  const [showRouteFormModal, setShowRouteFormModal] = useState(false);
  const [formType, setFormType] = useState<'add' | 'edit'>('add');
  const [editingRoute, setEditingRoute] = useState<RecordInTable | undefined>(
    undefined
  );
  const [period, setPeriod] = useState<RangePickerProps['value'] | null>(null);
  const [route, setRoute] = useState<string>(DEFAULT_OPTION.value);
  const [page, setPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: filterOptions, isLoading: isFilterOptionsLoading } = useSWR(
    '/ezcon/api/machineInspectionRecords/searchItemList',
    fetchRouteFilterOptions
  );

  const {
    data: tableData,
    isLoading: isTableDataLoading,
    mutate,
  } = useSWR(
    { url: '/ezcon/api/machineInspectionPlans', route, period, page, pageSize },
    fetchInspectionRouteList
  );

  const openRouteSettingsModal = (formType: 'add' | 'edit') => {
    setFormType(formType);
    setShowRouteFormModal(true);
  };

  const handleEditRouteButtonClick = (record: RecordInTable) => {
    openRouteSettingsModal('edit');
    setEditingRoute(record);
  };

  const closeModal = (modalName: string) => {
    setEditingRoute(undefined);
    if (modalName === 'routeForm') {
      setShowRouteFormModal(false);
    } else if (modalName === 'routeDetail') {
      setShowDetailModal(false);
    }
  };

  const handlePeriodChange = (values: RangePickerProps['value'] | null) => {
    const isValidPeriod = checkIsValidPeriod(values);
    if (isValidPeriod) {
      const [start, end] = values;
      setPeriod([start.startOf('day'), end.endOf('day')]);
    } else {
      setPeriod(values);
    }
  };

  const handleCheckDetailButtonClick = (record: RecordInTable) => {
    setEditingRoute(record);
    setShowDetailModal(true);
  };

  const deleteEquipment = async (equipmentId: string) => {
    try {
      const res = await fetch(
        `/ezcon/api/machineInspectionPlans/devices/${equipmentId}`,
        {
          method: 'DELETE',
          headers: {
            authorization: localStorage.getItem('feathers-jwt') || '',
          },
        }
      );

      if (!res.ok) {
        throw new Error('刪除失敗');
      }

      await mutate();
      message.success('刪除設備成功!');
    } catch (error) {
      console.error('刪除機電設備類別失敗:', error);
      message.error('刪除設備失敗!');
    }
  };

  const tableColumns = getTableColumns(COLUMNS, (record) =>
    record.action ? (
      <TableActionButtons
        buttons={record.action}
        clickEditButton={() => handleEditRouteButtonClick(record)}
        checkDetail={() => handleCheckDetailButtonClick(record)}
        deleteRoute={() => handleDeleteConfirm(record.id, mutate)}
      />
    ) : null
  );

  const routeOptions = useMemo(() => {
    return filterOptions
      ? getSortedRouteOptions(filterOptions.routeNameList)
      : [DEFAULT_OPTION];
  }, [filterOptions]);

  if (isFilterOptionsLoading || isTableDataLoading) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  const total = tableData ? tableData.totalPages * pageSize : 0;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.functionButtons}>
          <Button onClick={() => openRouteSettingsModal('add')}>
            新增路線
          </Button>
          <EditCategories
            categoryName="機電設備"
            buttonLabel="編輯機電設備"
            dialogTitle="編輯機電設備"
            downloadQrcode={async (equipmentId) =>
              await showQrcode(equipmentId)
            }
            onDelete={deleteEquipment}
          />
          <Button onClick={async () => await showQrcode()}>
            下載所有設備QRCode
          </Button>
        </div>
        <div className={styles.filters}>
          <div className={styles.filterContainer}>
            <p>日期：</p>
            <DateRangePicker
              placeholder={['開始日期', '結束日期']}
              defaultValue={period}
              onChange={handlePeriodChange}
            />
          </div>
          <div className={styles.filterContainer}>
            <p>路線：</p>
            <Select
              placeholder="請選擇路線"
              className={styles.selector}
              options={routeOptions}
              defaultValue={route}
              onChange={setRoute}
            />
          </div>
        </div>
        <Card free>
          <Card.Content>
            <Table
              rowKey="id"
              scroll={{ x: 'max-content' }}
              columns={tableColumns}
              dataSource={tableData?.data || []}
              pagination={{
                current: page,
                pageSize,
                total,
                onChange: setPage,
              }}
            />
          </Card.Content>
        </Card>
      </div>
      <DetailModal
        open={showDetailModal}
        closeModal={() => closeModal('routeDetail')}
        routeId={editingRoute ? editingRoute.id : ''}
      />
      <RouteFormModal
        open={showRouteFormModal}
        closeModal={() => closeModal('routeForm')}
        formType={formType}
        editingRoute={editingRoute}
        revalidateRouteTableData={mutate}
      />
    </>
  );
};

export default EquipmentInspectionManagement;
